/**
 * Printing system TypeScript type definitions
 * Matches the C# DTOs in BizClick.WMS.Core/Interfaces/IPrintingService.cs
 * and BizClick.WMS.Core/Interfaces/IPrintConfigurationService.cs
 */

// ==================== CONFIGURATION ====================

/**
 * Resolved print configuration from 4-level hierarchical lookup
 * Hierarchy: PackZone -> ProfileReports -> Profile -> Reports
 */
export interface PrintConfiguration {
  /** Resolved printer name */
  printerName: string | null;
  /** PrintNode printer ID (resolved from printer name) */
  printNodePrinterId: number | null;
  /** Number of copies to print */
  numOfCopies: number;
  /** Page width in millimeters */
  pageWidthMm: number | null;
  /** Page height in millimeters */
  pageHeightMm: number | null;
  /** Whether to use landscape orientation */
  isLandscape: boolean | null;
  /** Report type (1=ItemLabel, 2=ItemWithPO, etc.) */
  reportType: number;
  /** Report name/template identifier */
  reportName: string | null;
  /** Which hierarchy level the configuration was resolved from */
  resolvedFrom: 'PackZone' | 'ProfileReports' | 'Profile' | 'Reports' | '';
  /** Whether the configuration was successfully resolved */
  isResolved: boolean;
}

/**
 * Profile-specific report configuration override
 */
export interface ProfileReports {
  id: number;
  companyNo: string;
  profileID: string;
  reportID: number;
  printerName: string | null;
  printerNo: number | null;
  cloudStorageYesNo: boolean | null;
  cloudStorageItemLabelFolder: string | null;
  createdBy: string | null;
  created: string | null;
  updatedBy: string | null;
  lastUpdate: string | null;
}

/**
 * Request for creating a profile report configuration
 */
export interface CreateProfileReportRequest {
  profileId: string;
  reportId: number;
  printerName?: string | null;
  printerNo?: number | null;
  cloudStorageYesNo?: boolean | null;
  cloudStorageItemLabelFolder?: string | null;
}

/**
 * Request for updating a profile report configuration
 */
export interface UpdateProfileReportRequest {
  printerName?: string | null;
  printerNo?: number | null;
  cloudStorageYesNo?: boolean | null;
  cloudStorageItemLabelFolder?: string | null;
}

// ==================== REQUEST BASE ====================

/**
 * Base request for all print operations
 */
export interface PrintRequestBase {
  /** Report ID to use. If null, will use default for the item/entity */
  reportId?: number | null;
  /** Number of labels to print. Default is 1 */
  numOfCopies?: number;
  /** Profile ID for hierarchical configuration resolution */
  profileId?: string | null;
  /** Pack zone ID for hierarchical configuration resolution */
  packZoneId?: string | null;
  /** Override printer ID (bypasses hierarchical resolution) */
  printerIdOverride?: number | null;
  /**
   * Operatør-utfylte frifelt for custom LabelDesigner-maler.
   * Nøkkel = plassholder ("FreeText1"–"FreeText5"), verdi = tekst som settes inn i malen.
   * Fylles ut i FreeTextFieldsModal før utskrift når malen inneholder frifelt.
   */
  freeTextValues?: Record<string, string>;
}

// ==================== LABEL REQUESTS ====================

/**
 * Request for printing item labels (ReportType 1)
 */
export interface PrintItemLabelRequest extends PrintRequestBase {
  itemNo: string;
  itemDescription?: string | null;
  batch?: string | null;
  expiryDate?: string | null;
  weight?: number | null;
  quantity?: number | null;
  locationName?: string | null;
  /** Lageret operatøren står på (useWarehouse().currentWarehouse). Gir varens standardlokasjon
   *  på lageret ({{BinCode}}, {{LocationName}}, {{StcBin.*}}). Null = «Alle lager», uendret etikett. */
  warehouseId?: string | null;
  // Display options
  showHeader?: boolean;
  showBarcode?: boolean;
  showDescription?: boolean;
}

/**
 * Request for printing item labels with purchase order (ReportType 2)
 */
export interface PrintItemLabelWithPORequest extends PrintItemLabelRequest {
  pOrderNo?: number | null;
  pOrderLineNo?: number | null;
  supplierName?: string | null;
  supplierItemNo?: string | null;
  receivedDate?: string | null;
  /** Allokering (TW 36362298): ResID for allokeringsreservasjonen — gir malen
   *  {{AllocationBarcode}} («ALC-{ResID}»). Null = vanlig vareetikett. */
  allocationResID?: number | null;
}

/**
 * Request for printing item labels with sales order (ReportType 3)
 */
export interface PrintItemLabelWithSORequest extends PrintItemLabelRequest {
  orderNo?: number | null;
  orderNoERP?: string | null;
  customerName?: string | null;
  orderDate?: string | null;
}

/**
 * Request for printing location labels (ReportType 4/5)
 */
export interface PrintLocationLabelRequest extends PrintRequestBase {
  locationId: string;
  locationName?: string | null;
  binCode?: string | null;
  warehouseId?: string | null;
  zoneName?: string | null;
  includeBarcode?: boolean;
  // Display options
  showHeader?: boolean;
  showDescription?: boolean;
  showZone?: boolean;
  description?: string | null;
}

/**
 * Request for printing location labels for a batch of bin codes (ReportType 4/5).
 * Felles rapport-/skriver-oppsett (PrintRequestBase) brukes for alle etikettene.
 */
export interface PrintLocationLabelBatchRequest extends PrintRequestBase {
  binCodes: string[];
  warehouseId?: string | null;
}

/**
 * Aggregert resultat for batch-utskrift av lokasjonsetiketter.
 */
export interface PrintLocationLabelBatchResult {
  success: boolean;
  requestedCount: number;
  printedCount: number;
  /** Feil per bincode: "BINCODE: melding" */
  errors: string[];
}

/**
 * Request for printing a generic label (Norlux P5, ReportType 60 Diverse) — collection-box, fritekst-etiketter m.m.
 */
export interface PrintGenericLabelRequest extends PrintRequestBase {
  /** Valgfri fritekst fra operatør. Mappes til mal-feltet "FreeText". */
  freeText?: string | null;
}

/**
 * Request for printing package/colli labels
 */
export interface PrintPackageLabelRequest extends PrintRequestBase {
  packageId: number;
  pListId?: number | null;
  orderNo?: number | null;
  orderNoERP?: string | null;
  customerName?: string | null;
  deliveryAddress?: string | null;
  postalCode?: string | null;
  city?: string | null;
  colliNumber?: number;
  totalColli?: number;
  weight?: number | null;
  sscc?: string | null;
}

/**
 * SSCC label type (matches legacy PLSTAND005Reports bitmask)
 */
export enum SSCCLabelType {
  StandardPallet = 1,
  Transport = 2
}

/**
 * Request for printing SSCC/pallet labels with GS1-128 barcodes
 */
export interface PrintSSCCLabelRequest extends PrintRequestBase {
  sscc: string;
  gtin?: string | null;
  batchNo?: string | null;
  expiryDate?: string | null;
  quantity?: number | null;
  customerName?: string | null;
  orderNoERP?: string | null;
  deliveryAddress?: string | null;
  /** PList-ID for pallen — brukes med SSCC til å slå opp riktig OrderSSCC-rad i custom-etikett. */
  pListId?: number | null;
  /** SSCC label type based on PLSTAND005Reports bitmask */
  labelType?: SSCCLabelType;
  /** Innholdsetikett (36334106): inkluder varelinjene på pallen (inntil 12 + «+N flere»). */
  includeContent?: boolean;
}

// ==================== DOCUMENT REQUESTS ====================

/**
 * Line item for packing slip
 */
export interface PackingSlipLine {
  lineNo: number;
  itemNo: string;
  itemDescription?: string | null;
  qtyOrdered: number;
  qtyPicked: number;
  location?: string | null;
}

/**
 * Request for printing packing slips
 */
export interface PrintPackingSlipRequest extends PrintRequestBase {
  pListId: number;
  customerName?: string | null;
  deliveryAddress?: string | null;
  packingDate?: string | null;
  lines?: PackingSlipLine[];
  totalWeight?: number | null;
  totalColli?: number | null;
  carrierName?: string | null;
  remarks?: string | null;
}

/**
 * Pakkseddel per forsendelse (pakkemodul) eller per plukkliste når forsendelse mangler.
 * Hode og linjer hentes fra databasen (Shipment, PListOrders, Orders, OrderLine) — ingen
 * linjer sendes fra klienten. Én av shipmentId/pListId må være satt.
 */
export interface PrintShipmentPackingSlipRequest extends PrintRequestBase {
  shipmentId?: number | null;
  pListId?: number | null;
  /** ISO-dato. Null = nå. */
  packingDate?: string | null;
}

/**
 * Single package item for batch label printing
 */
export interface BatchPackageLabelItem {
  /** The internal package number (used for barcode) */
  packageNo: number;
  /** The visible/display package number (shown large on label) */
  visiblePackageNo: number;
}

/**
 * Request for printing batch package labels (for batch picking)
 * Simple labels showing VisiblePackageNo and PackageNo barcode
 */
export interface PrintBatchPackageLabelsRequest extends PrintRequestBase {
  /** The picking list ID these packages belong to */
  pListId: number;
  /** List of packages to print labels for */
  packages: BatchPackageLabelItem[];
}

/**
 * Request for printing a sorting box label (sorteringskasse).
 * One label per zone with the assigned StcBoxNo as Code128 barcode.
 */
export interface PrintSortingBoxLabelRequest extends PrintRequestBase {
  /** Zone identifier (e.g. "3" for "Tralle") */
  stcBoxTypeID: string;
  /** Optional human-readable description (e.g. "Tralle") */
  description?: string;
  /** The sorting box number (used for both display text and Code128 barcode) */
  stcBoxNo: number;
}

// ==================== RESPONSE ====================

/**
 * Result of a print operation
 */
export interface PrintResult {
  success: boolean;
  message: string;
  /** Ikke-blokkerende advarsel ved vellykket utskrift (f.eks. innholdsetikett uten innholdstabell). */
  warning?: string | null;
  printLogId?: number | null;
  printNodeJobId?: number | null;
  labelsPrinted: number;
}

// ==================== PRINT LOG ====================

/**
 * Print job status
 */
export type PrintLogStatus = 'Queued' | 'Success' | 'Failed';

/**
 * Reference types for print log entries
 */
export type PrintLogReferenceType = 'Item' | 'Location' | 'Package' | 'SSCC' | 'PackingSlip' | 'ShippingLabel' | 'TransportLabel' | 'Reprint';

/**
 * Print log entry for audit trail
 */
export interface PrintLog {
  logID: number;
  companyNo: string;
  reportID: number;
  reportType: number;
  printerName: string | null;
  printNodeJobId: number | null;
  profileID: string | null;
  operatorID: string | null;
  printedDateTime: string;
  numOfCopies: number;
  status: PrintLogStatus;
  errorMessage: string | null;
  referenceType: PrintLogReferenceType | null;
  referenceID: string | null;
  /** Path to the archived PDF file on disk for reprint capability */
  pdfFilePath: string | null;
  /** For reprints: the LogID of the original print job */
  originalLogID: number | null;
  /** Report template name (Reports.ReportName) — resolved server-side on history reads */
  reportName: string | null;
  /** Report template description (Reports.Description) — resolved server-side on history reads */
  reportDescription: string | null;
}

// ==================== REPORTS ====================

/**
 * Report definition
 */
export interface Report {
  reportID: number;
  companyNo: string;
  description: string;
  reportType: number;
  reportName: string | null;
  printerName: string | null;
  numOfCopies: number;
  pageWidthMm: number | null;
  pageHeightMm: number | null;
  isLandscape: boolean;
  isActive: boolean;
  /** Reports.ActiveYesNo — bestemmer om rapporten vises i utskriftsdialoger. */
  activeYesNo: boolean;
  /**
   * JSON-serialisert layout fra LabelDesigner. Null = innebygd generator.
   * Brukes bl.a. til å detektere frifelt (FreeText1-5) før utskrift.
   */
  layoutJson?: string | null;
}

/**
 * Report types enum
 */
export enum ReportType {
  ItemLabel = 1,
  ItemLabelWithPO = 2,
  ItemLabelWithSO = 3,
  LocationLabel = 4,
  /** Diverse/generisk etikett — custom LabelDesigner-mal, tilgjengelig i alle moduler */
  Diverse = 60,
  External = 99
}

// ==================== PACKAGE LABEL CREATION ====================

/**
 * Request for creating package labels and inserting PListOrderPackages records.
 * Used when printing kolli-etiketter from picking module.
 * Creates X separate rows in PListOrderPackages (one per label) following legacy pattern.
 */
export interface CreatePackageLabelsRequest {
  /** Number of package labels to create and print. Each creates one row with unique PackageID */
  numberOfLabels: number;
  /** PListID for standalone picks (required for PListOrderPackages) */
  pListID: number;
  /** OrderNo for standalone picks (required for PListOrderPackages) */
  orderNo: number;
  /** Samplukk: alle ordrene som deler esken til linjen. Flere enn én → felles PackageID per ny eske (TW 36438546) */
  sharedOrderNos?: number[];
  /** Pick type: "Standalone" for PListOrderPackages, "Batch" for Packages table */
  pickType: 'Standalone' | 'Batch';
  /** Optional: Customer name for label printing */
  customerName?: string | null;
  /** Optional: ERP order number for label display */
  orderNoERP?: string | null;
  /** Optional: Delivery address for label printing */
  deliveryAddress?: string | null;
  /** Whether to automatically print labels after creation (default true) */
  printLabels?: boolean;
  /** Optional: Override report ID for label template */
  reportIdOverride?: number | null;
  /** Optional: Override printer ID */
  printerIdOverride?: number | null;
}

/**
 * Information about a single created package
 */
export interface CreatedPackageInfo {
  /** Sequential package number within this order (1, 2, 3, etc.) */
  pListOrderPackageID: number;
  /** Globally unique package ID (the scannable barcode number) */
  packageID: number;
  /** Display text: "Kolli X av Y" */
  displayText: string;
}

/**
 * Response from package label creation operation
 */
export interface CreatePackageLabelsResponse {
  /** Whether the operation was successful */
  success: boolean;
  /** Message describing the result */
  message: string;
  /** Number of packages created in database */
  packagesCreated: number;
  /** Number of labels successfully printed */
  labelsPrinted: number;
  /** List of created packages with their IDs */
  createdPackages: CreatedPackageInfo[];
  /** Print job ID if labels were sent to printer */
  printJobId?: string | null;
}

// ==================== REPRINT ====================

/**
 * Request to reprint a previously printed document
 */
export interface ReprintRequest {
  /** The original PrintLog LogID to reprint */
  originalLogId: number;
  /** Override printer ID for reprint */
  printerIdOverride?: number | null;
  /** Number of copies (uses original if not specified) */
  numOfCopies?: number | null;
}

/**
 * Result of a cleanup operation
 */
export interface CleanupResult {
  logsDeleted: number;
  filesDeleted: number;
  fileDeleteErrors: number;
}

/**
 * Search parameters for print history
 */
export interface PrintHistorySearchParams {
  referenceType?: string | null;
  referenceId?: string | null;
  operatorId?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  limit?: number;
  offset?: number;
}

export interface PaginatedPrintHistory {
  items: PrintLog[];
  totalCount: number;
}

// ==================== PROCESS DOCUMENT REQUESTS ====================

/**
 * Request for printing a picking list document
 */
export interface PrintPickingListRequest extends PrintRequestBase {
  pListId: number;
}

/**
 * Request for printing an inventory count list document
 */
export interface PrintCountListRequest extends PrintRequestBase {
  warehouseId?: string | null;
  zoneId?: string | null;
  blindCount?: boolean;
  lines?: CountListLineItem[];
}

export interface CountListLineItem {
  binCode: string;
  itemNo: string;
  itemDescription?: string | null;
  expectedQty?: number | null;
  batch?: string | null;
}

/**
 * Request for printing a putaway list document
 */
export interface PrintPutawayListRequest extends PrintRequestBase {
  pOrderNo?: number | null;
  lines?: PutawayListLineItem[];
}

export interface PutawayListLineItem {
  itemNo: string;
  itemDescription?: string | null;
  quantity: number;
  sourceLocation?: string | null;
  destinationLocation?: string | null;
  batch?: string | null;
}

// ==================== TABLE EXPORT ====================

/**
 * Request for generating a generic table PDF
 */
export interface TablePdfExportRequest {
  title: string;
  isLandscape?: boolean;
  columns: TablePdfColumn[];
  rows: Record<string, any>[];
  footerText?: string;
}

export interface TablePdfColumn {
  key: string;
  label: string;
  alignment?: 'left' | 'center' | 'right';
  relativeWidth?: number;
}
